import { Link } from "@tanstack/react-router";
import type { Contact } from "@/lib/types";
import { formatCents, formatDate, formatDateTime } from "@/lib/format";
import { isCardless, methodLabel } from "@/lib/payments";
import { StripeLink } from "@/components/StripeLink";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAutomationPositions, useBillingSummary, useUpcomingEvents } from "./hooks";
import { SignInsCard } from "./SignInsCard";
import { ForumsCard } from "./ForumsCard";
import { useToast } from "@/components/Toaster";
import { api, ApiError } from "@/lib/api";
import { SkipMoveButton } from "@/features/automations/RunSkip";

/** Right rail: billing summary, each automation with the person's exact position, upcoming events.
 *  A card that can't load says so in one line. */
export function RightRail({ c }: { c: Contact }) {
  const billing = useBillingSummary(c.id);
  const autos = useAutomationPositions(c.id);
  const events = useUpcomingEvents(c.id);
  const b = billing.data;
  return (
    <div className="space-y-3">
      <Card>
        <CardHeader>
          <CardTitle>Billing</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          {billing.error ? (
            <p className="text-[var(--color-destructive)]">Couldn't load billing: {billing.error.message}</p>
          ) : !b ? (
            <Skeleton className="h-16" />
          ) : (
            <>
              <Row k="Next charge" v={b.next_charge ? `${formatCents(b.next_charge.amount_cents)} on ${formatDate(b.next_charge.date)}` : "None scheduled"} />
              {b.next_charge && <Row k="Plan" v={b.next_charge.plan} />}
              <Row
                k="Card"
                v={
                  b.card ? (
                    <>
                      {methodLabel(b.card)}
                      {!isCardless(b.card) && b.card.exp ? `, exp ${b.card.exp}` : ""}
                      {b.card.status && b.card.status !== "active" && (
                        <Badge variant="destructive" className="ml-1.5">
                          {b.card.status}
                        </Badge>
                      )}
                    </>
                  ) : (
                    "No saved card"
                  )
                }
              />
              <Row k="Open invoices" v={b.open_invoices ? `${b.open_invoices}${b.overdue_cents ? ` (${formatCents(b.overdue_cents)} overdue)` : ""}` : "None"} />
              {!!b.open_disputes && (
                <Link
                  to="/contacts/$contactId"
                  params={{ contactId: String(c.id) }}
                  search={{ view: "billing" }}
                  className="flex items-center gap-1.5 pt-1 text-xs hover:underline"
                  title="The customer's bank opened a chargeback. Answer it in Stripe's dashboard before its deadline."
                >
                  <Badge variant="destructive">disputed</Badge>
                  <span className="text-[var(--color-danger-fg)]">
                    {b.open_disputes} open chargeback{b.open_disputes > 1 ? "s" : ""}
                  </span>
                </Link>
              )}
              {!!b.pending_approvals && (
                <Link to="/billing/approvals" className="block pt-1 text-xs font-medium text-[var(--color-warning-fg)] hover:underline">
                  {b.pending_approvals} billing decision{b.pending_approvals > 1 ? "s" : ""} waiting for approval
                </Link>
              )}
              {b.card_link_available && <CopyLink contactId={c.id} brandId={b.card_link_brand_id} />}
              <StripeLink url={b.stripe_customer_url ?? b.card?.dashboard_url} label="Customer in Stripe" what="This customer" className="pt-1" />
            </>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Automations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2.5 text-sm">
          {autos.isLoading && <Skeleton className="h-12" />}
          {autos.error && <p className="text-[var(--color-destructive)]">Couldn't load automations: {autos.error.message}</p>}
          {autos.data?.length === 0 && <p className="text-[var(--color-muted-foreground)]">Not in any automation.</p>}
          {autos.data?.map((a) => {
            const live = a.status === "active" || a.status === "waiting";
            return (
              <div key={a.enrollment_id}>
                <div className="flex items-center justify-between gap-2">
                  <Link to="/automations/runs/$runId" params={{ runId: String(a.enrollment_id) }} className="font-medium text-[var(--color-primary)] hover:underline" title="Open this run: step log, pause, retry, take out">
                    {a.automation_name}
                  </Link>
                  <span className="flex gap-1">
                    {a.shadow && (
                      <Badge variant="muted" title="Shadow mode: logs what it would do, sends nothing">
                        shadow
                      </Badge>
                    )}
                    <Badge variant={live ? "success" : "muted"}>{a.status}</Badge>
                  </span>
                </div>
                {a.step_count > 0 && (
                  <div className="mt-1 flex items-center gap-2">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--color-muted)]" aria-hidden>
                      <div className="h-full rounded-full bg-[var(--color-primary)]" style={{ width: `${((a.step_number || a.step_count) / Math.max(a.step_count, 1)) * 100}%` }} />
                    </div>
                    <span className="num text-xs text-[var(--color-muted-foreground)]">{a.step_number ? `step ${a.step_number} of ${a.step_count}` : "finished"}</span>
                  </div>
                )}
                <div className="text-xs text-[var(--color-muted-foreground)]">
                  {(a.summary || a.step_label).replace(/^\[shadow\]\s*/, "")}
                  {!a.summary && a.next_run_at && live ? `; next ${formatDateTime(a.next_run_at)}` : ""}
                  {a.exit_reason ? ` (${a.exit_reason})` : ""}
                </div>
                {(a.skipping ?? []).map((m) => (
                  <div key={m.step_key} className="text-xs text-[var(--color-warning-fg)]">
                    Will skip: {m.subject || m.step_label}
                  </div>
                ))}
                {(live || a.status === "paused" || a.status === "failed") && (
                  <SkipMoveButton runId={a.enrollment_id} who={[c.first_name, c.last_name].filter(Boolean).join(" ") || "this person"} variant="link" className="h-6 px-0 text-xs" />
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
      <SignInsCard c={c} />
      <ForumsCard contactId={c.id} />
      <Card>
        <CardHeader>
          <CardTitle>Upcoming events</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {events.isLoading && <Skeleton className="h-12" />}
          {events.error && <p className="text-[var(--color-destructive)]">Couldn't load events: {events.error.message}</p>}
          {events.data?.length === 0 && <p className="text-[var(--color-muted-foreground)]">No upcoming events.</p>}
          {events.data?.map((e) => (
            <div key={e.registration_id}>
              <Link to="/events/$eventId" params={{ eventId: String(e.event_id) }} className="font-medium text-[var(--color-primary)] hover:underline">
                {e.event}
              </Link>
              <div className="text-xs text-[var(--color-muted-foreground)]">
                {e.when || formatDateTime(e.starts_at)}
                {e.location ? `, ${e.location}` : ""}
              </div>
              <div className="mt-0.5 flex flex-wrap gap-1.5">
                <Badge variant={e.status === "registered" || e.status === "attended" ? "success" : e.status === "waitlisted" ? "warning" : "muted"}>{e.status.replace("_", " ")}</Badge>
                {e.checked_in_at && <Badge variant="success">checked in</Badge>}
                {e.guests > 0 && <Badge variant="muted">+{e.guests} guest{e.guests > 1 ? "s" : ""}</Badge>}
                {e.join_url && (
                  <a href={e.join_url} target="_blank" rel="noreferrer noopener" className="text-xs text-[var(--color-primary)] hover:underline">
                    Demio link
                  </a>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-[var(--color-muted-foreground)]">{k}</span>
      <span className="text-right">{v}</span>
    </div>
  );
}

/** The customer's update-card link signs them in to the portal, so staff copy it to send; it isn't opened here.
 *  It is made only on click (billing_card_update_link, audited), never sent with the billing summary. */
function CopyLink({ contactId, brandId }: { contactId: number; brandId?: number }) {
  const toast = useToast();
  const copy = () => {
    const link = api<{ url: string }>(`/api/v1/contacts/${contactId}/card-update-link`, {
      method: "POST",
      json: brandId ? { brand_id: brandId } : {},
    }).then((r) => r.url);
    // Safari only lets a click write the clipboard if the write starts right away: hand it the pending link.
    const write =
      typeof ClipboardItem !== "undefined" && navigator.clipboard?.write
        ? navigator.clipboard.write([new ClipboardItem({ "text/plain": link.then((u) => new Blob([u], { type: "text/plain" })) })])
        : link.then((u) => navigator.clipboard?.writeText(u));
    write.then(
      () => toast("Copied the update-card link"),
      (e: unknown) => toast(e instanceof ApiError ? e.message : "Couldn't copy the update-card link", "error"),
    );
  };
  return (
    <button
      className="block pt-1 text-left text-xs text-[var(--color-primary)] hover:underline"
      title="Makes a sign-in link for the customer's account page (card on file). Works for 7 days. Send it to them; opening it here would sign you in as them."
      onClick={copy}
    >
      Copy update-card link for the customer
    </button>
  );
}
